// "Press Kit & Assets" section for theme pages (and the assign-to-theme modal
// used from both theme pages and the project detail page).
//
// A section is scoped by (themeId, section): plain themes use section=null,
// the material theme uses its sub-tab key (materials/details/craft), practice
// uses studio/monograph, and collaborators use the collaborator's comms_id.
// Linked Dropbox folders live in comms.theme_folders; pinned highlight files
// live in comms.theme_assets (thumbnails cached, Dropbox path is the source
// of truth).
import React, { useState, useEffect } from 'react';
import { ChevronRight, ChevronDown, Trash2, Film, FileText, FolderPlus, Hash, ImagePlus, ExternalLink } from 'lucide-react';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, useSortable, arrayMove, rectSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { supabase } from '../../supabaseClient';
import { commsInsert, commsUpdate, commsDelete } from './commsUtils';
import { readVersionPref } from './assetVariants';
import { S, GOLD, Chip, FilterBtn, InlineInput, EmptyNote, BtnGhost, BtnPrimary, CommsModal, Field } from './commsUi';
import {
  BRIO_PRESS_BASE, PRESS_KIT_DEFAULT_SCHEMA, callEdgeFunction, isPdf,
  Lightbox, FolderTree, usePressFolderBrowser, useAssetSelection, SelectBar,
  addAssetsToTheme, AssignToPostModal, TagAssetsModal, themeTagsFor, useAssetTags,
} from './pressAssets';

const comms = supabase.schema('comms');

// Small square icon button matching the press-kit header controls on the
// project detail page.
function IconBtn({ onClick, title, active, children }) {
  return (
    <button onClick={onClick} title={title}
      style={{ background: active ? GOLD : 'none', color: active ? '#fff' : '#6E6C66', border: active ? `1px solid ${GOLD}` : '1px solid rgba(27,27,25,.2)', borderRadius: 2, cursor: 'pointer', padding: '2px 4px', lineHeight: 1, display: 'flex', alignItems: 'center' }}>
      {children}
    </button>
  );
}

// Derive the source project of a Dropbox path: prefer the linked folder the
// path came from, otherwise match the "<CODE>_<Name>" top-level folder.
function projectCodeForPath(path, folders, projects) {
  const fromFolder = (folders || []).find(f => path === f.dropbox_path || path.startsWith(f.dropbox_path + '/'));
  if (fromFolder?.project_code) return fromFolder.project_code;
  if (path.startsWith(BRIO_PRESS_BASE + '/')) {
    const top = path.slice(BRIO_PRESS_BASE.length + 1).split('/')[0];
    const hit = (projects || []).find(p => p.project_code && top.startsWith(`${p.project_code}_`));
    if (hit) return hit.project_code;
  }
  return null;
}

// ── HIGHLIGHT TILE ─────────────────────────────────────────────────────────

// Thumbnail-or-icon body shared by highlight tiles and filter-card tiles;
// rows come from theme_assets / asset_tags (cached thumb_url, no Dropbox hit).
function tileMeta(row) {
  const name = row.file_name || row.dropbox_path.split('/').pop();
  return {
    name,
    // Carried through so a re-assignment out of a theme or filter card writes
    // the stable key, exactly as a selection made in a folder listing does.
    fileId: row.dropbox_file_id || null,
    thumbUrl: row.thumb_url || null,
    isVideo: !!row.is_video,
    isPdf: isPdf(name),
    projectCode: row.project_code || null,
  };
}

function TileBody({ row, name, dimmed }) {
  return (
    <>
      {row.thumb_url
        ? <img src={row.thumb_url} alt={name} style={{ width: '100%', aspectRatio: '3/2', objectFit: 'cover', display: 'block', opacity: dimmed ? 0.75 : 1 }} />
        : <div style={{ width: '100%', aspectRatio: '3/2', background: '#E8E5DD', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9A988F' }}>
            {row.is_video ? <Film size={18} /> : isPdf(name) ? <FileText size={18} /> : <ExternalLink size={16} />}
          </div>
      }
      {row.is_video && row.thumb_url && (
        <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.22)' }}>
          <svg width="10" height="12" viewBox="0 0 10 12" fill="none"><path d="M1 1l8 5-8 5V1z" fill="white"/></svg>
        </div>
      )}
    </>
  );
}

function SortableHighlightTile({ asset, canEdit, selectMode, selection, projects, onOpen, onRemove, onSaveCaption }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: asset.comms_id, disabled: !canEdit || selectMode });
  const selected = selectMode && selection?.has(asset.dropbox_path);
  const project = (projects || []).find(p => p.project_code === asset.project_code);
  const name = asset.file_name || asset.dropbox_path.split('/').pop();
  const handleClick = selectMode
    ? () => selection.toggle(asset.dropbox_path, tileMeta(asset))
    : () => onOpen(asset);
  return (
    <div ref={setNodeRef} {...attributes} {...listeners}
      style={{ transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.4 : 1, touchAction: 'manipulation' }}>
      <div
        onClick={handleClick}
        title={[name, asset.credit].filter(Boolean).join(' — ')}
        style={{ position: 'relative', borderRadius: 2, overflow: 'hidden', cursor: 'pointer', outline: selected ? `2px solid ${GOLD}` : 'none', outlineOffset: -2, lineHeight: 0 }}>
        <TileBody row={asset} name={name} dimmed={selectMode && !selected} />
        {selectMode && (
          <span style={{ position: 'absolute', top: 4, right: 4, width: 16, height: 16, borderRadius: '50%', background: selected ? GOLD : 'rgba(255,255,255,.75)', border: selected ? 'none' : '1px solid rgba(27,27,25,.3)', color: '#fff', fontSize: 11, lineHeight: '16px', textAlign: 'center', fontFamily: "'IBM Plex Mono', monospace" }}>
            {selected ? '✓' : ''}
          </span>
        )}
        {project && (
          <span style={{ position: 'absolute', left: 4, bottom: 4, fontFamily: "'IBM Plex Mono', monospace", fontSize: 8, letterSpacing: '0.06em', textTransform: 'uppercase', background: 'rgba(27,27,25,.65)', color: '#F7F6F2', borderRadius: 2, padding: '2px 5px', lineHeight: 1.2 }}>
            {project.project_code}
          </span>
        )}
        {canEdit && !selectMode && (
          <button onClick={e => { e.stopPropagation(); onRemove(asset); }} title="Remove from highlights"
            style={{ position: 'absolute', top: 4, right: 4, background: 'rgba(27,27,25,.55)', border: 'none', borderRadius: 2, color: '#F7F6F2', cursor: 'pointer', padding: 3, lineHeight: 0 }}>
            <Trash2 size={11} />
          </button>
        )}
      </div>
      {canEdit && !selectMode
        ? <div onClick={e => e.stopPropagation()} onPointerDown={e => e.stopPropagation()}>
            <InlineInput
              value={asset.caption || ''}
              onBlurSave={v => onSaveCaption(asset, v)}
              placeholder="Caption…"
              style={{ border: 'none', background: 'transparent', padding: '3px 1px 0', fontSize: 10.5, fontFamily: "'IBM Plex Mono', monospace", color: '#6E6C66' }}
            />
          </div>
        : asset.caption
          ? <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 10.5, color: '#6E6C66', padding: '3px 1px 0', lineHeight: 1.4 }}>{asset.caption}</div>
          : null
      }
    </div>
  );
}

// Read-only tile for hashtag-filter cards (assets from comms.asset_tags).
function FilterAssetTile({ row, selectMode, selection, onOpen }) {
  const name = row.file_name || row.dropbox_path.split('/').pop();
  const selected = selectMode && selection?.has(row.dropbox_path);
  const handleClick = selectMode
    ? () => selection.toggle(row.dropbox_path, tileMeta(row))
    : () => onOpen(row);
  return (
    <div onClick={handleClick} title={`${name}${row.project_code ? ` — ${row.project_code}` : ''}`}
      style={{ position: 'relative', borderRadius: 2, overflow: 'hidden', cursor: 'pointer', outline: selected ? `2px solid ${GOLD}` : 'none', outlineOffset: -2, lineHeight: 0 }}>
      <TileBody row={row} name={name} dimmed={selectMode && !selected} />
      {selectMode && (
        <span style={{ position: 'absolute', top: 4, right: 4, width: 16, height: 16, borderRadius: '50%', background: selected ? GOLD : 'rgba(255,255,255,.75)', border: selected ? 'none' : '1px solid rgba(27,27,25,.3)', color: '#fff', fontSize: 11, lineHeight: '16px', textAlign: 'center', fontFamily: "'IBM Plex Mono', monospace" }}>
          {selected ? '✓' : ''}
        </span>
      )}
      {row.project_code && (
        <span style={{ position: 'absolute', left: 4, bottom: 4, fontFamily: "'IBM Plex Mono', monospace", fontSize: 8, letterSpacing: '0.06em', textTransform: 'uppercase', background: 'rgba(27,27,25,.65)', color: '#F7F6F2', borderRadius: 2, padding: '2px 5px', lineHeight: 1.2 }}>
          {row.project_code}
        </span>
      )}
    </div>
  );
}

// ── LINK FOLDER PICKER ─────────────────────────────────────────────────────

function LinkFolderModal({ suggestions, existingPaths, onLink, onClose }) {
  const [path, setPath] = useState(BRIO_PRESS_BASE);
  const [state, setState] = useState({ loading: true, entries: [], error: null });
  const [linkingPath, setLinkingPath] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setState({ loading: true, entries: [], error: null });
    // This picker lists sub-folders only, so it skips per-file temporary links
    // and thumbnail generation entirely.
    callEdgeFunction('list-press-folder-contents', { folderPath: path, foldersOnly: true })
      .then(json => { if (!cancelled) setState({ loading: false, entries: json.entries || [], error: null }); })
      .catch(e => { if (!cancelled) setState({ loading: false, entries: [], error: e.message }); });
    return () => { cancelled = true; };
  }, [path]);

  const rel = path.slice(BRIO_PRESS_BASE.length).split('/').filter(Boolean);
  const folders = state.entries.filter(e => e.is_folder);
  const isLinked = p => existingPaths.has(p);

  const link = async (p) => {
    if (linkingPath || isLinked(p)) return;
    setLinkingPath(p);
    await onLink(p);
    setLinkingPath(null);
  };

  const crumbStyle = { background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontFamily: "'IBM Plex Mono', monospace", fontSize: 11, color: '#21302A' };

  return (
    <CommsModal title="Link a folder" onClose={onClose}>
      {suggestions.length > 0 && (
        <Field label="From projects linked to this theme">
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {suggestions.map(sg => (
              <button key={sg.path} onClick={() => setPath(sg.path)} style={{ ...crumbStyle, border: '1px solid rgba(27,27,25,.12)', borderRadius: 2, padding: '4px 8px' }}>
                {sg.label}
              </button>
            ))}
          </div>
        </Field>
      )}
      <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 4, marginBottom: 10 }}>
        <button onClick={() => setPath(BRIO_PRESS_BASE)} style={crumbStyle}>BRIO-press</button>
        {rel.map((seg, i) => (
          <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            <span style={{ color: '#9E9C96', fontSize: 11 }}>/</span>
            <button onClick={() => setPath(`${BRIO_PRESS_BASE}/${rel.slice(0, i + 1).join('/')}`)} style={crumbStyle}>{seg}</button>
          </span>
        ))}
      </div>
      <div style={{ maxHeight: 320, overflowY: 'auto', border: '1px solid rgba(27,27,25,.1)', borderRadius: 3, background: '#F7F6F2' }}>
        {state.loading && <div style={{ ...S.meta, padding: 12 }}>Loading…</div>}
        {state.error && <div style={{ ...S.meta, padding: 12, color: '#C0392B' }}>{state.error}</div>}
        {!state.loading && !state.error && folders.length === 0 && <div style={{ ...S.meta, padding: 12 }}>No subfolders here.</div>}
        {folders.map(f => {
          const childPath = `${path}/${f.name}`;
          return (
            <div key={f.name} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, padding: '7px 12px', borderBottom: '1px solid rgba(27,27,25,.06)' }}>
              <button onClick={() => setPath(childPath)} style={{ ...crumbStyle, display: 'flex', alignItems: 'center', gap: 6, textAlign: 'left', flex: 1, minWidth: 0 }}>
                <ChevronRight size={11} style={{ flexShrink: 0, color: '#9E9C96' }} />
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.name}</span>
              </button>
              {isLinked(childPath)
                ? <Chip label="linked" cls="pub" />
                : <button onClick={() => link(childPath)} disabled={!!linkingPath}
                    style={{ ...crumbStyle, border: '1px solid #33473F', borderRadius: 2, padding: '3px 8px', color: '#21302A', opacity: linkingPath ? 0.5 : 1 }}>
                    {linkingPath === childPath ? 'Linking…' : '+ Link'}
                  </button>
              }
            </div>
          );
        })}
      </div>
      <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 18 }}>
        <BtnGhost onClick={onClose}>Done</BtnGhost>
        {path !== BRIO_PRESS_BASE && !isLinked(path) && (
          <BtnPrimary onClick={() => link(path)}>{linkingPath === path ? 'Linking…' : 'Link this folder'}</BtnPrimary>
        )}
      </div>
    </CommsModal>
  );
}

// ── HASHTAG FILTER PICKER ──────────────────────────────────────────────────
// Creates a filter card: a theme_folders row with filter_tags instead of a
// dropbox_path. The card lists every asset tagged with ALL chosen hashtags.

function AddFilterModal({ onCreate, onClose }) {
  const [options, setOptions] = useState(null);
  const [chosen, setChosen] = useState([]);
  const [label, setLabel] = useState('');
  const [query, setQuery] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    Promise.all([
      supabase.from('app_settings').select('value').eq('key', 'commsAssetHashtags').maybeSingle(),
      comms.from('themes').select('comms_id').order('sort_order'),
      comms.from('asset_tags').select('tags'),
    ]).then(([s, th, at]) => {
      let allowed = [];
      try {
        const v = typeof s.data?.value === 'string' ? JSON.parse(s.data.value) : s.data?.value;
        if (Array.isArray(v)) allowed = v;
      } catch {}
      const inUse = (at.data || []).flatMap(r => r.tags || []);
      setOptions([...new Set([...allowed, ...(th.data || []).map(t => t.comms_id), ...inUse])].sort((a, b) => a.localeCompare(b)));
    });
  }, []);

  const toggle = tag => setChosen(c => c.includes(tag) ? c.filter(x => x !== tag) : [...c, tag]);

  const create = async () => {
    if (busy || chosen.length === 0) return;
    setBusy(true);
    await onCreate(chosen, label.trim() || chosen.map(t => `#${t}`).join(' '));
  };

  const q = query.trim().toLowerCase().replace(/^#+/, '');
  const shown = (options || []).filter(tag => !q || tag.includes(q) || chosen.includes(tag));

  return (
    <CommsModal title="Add a hashtag filter" onClose={onClose} width={460}>
      <div style={{ ...S.body, marginBottom: 12 }}>
        The card will show every asset tagged with <strong>all</strong> of the chosen hashtags.
      </div>
      {options === null && <div style={S.meta}>Loading…</div>}
      {options !== null && options.length === 0 && <EmptyNote text="No hashtags exist yet — tag some assets first, or add allowed hashtags on Practice → Comms assets." />}
      {options !== null && options.length > 0 && (
        <input value={query} onChange={e => setQuery(e.target.value)} placeholder="Type to find a hashtag…" autoFocus
          style={{ ...S.inputStyle, marginBottom: 12 }} />
      )}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 14, maxHeight: 220, overflowY: 'auto' }}>
        {options !== null && options.length > 0 && shown.length === 0 && <span style={S.meta}>No hashtag matches “{query}”.</span>}
        {shown.map(tag => (
          <FilterBtn key={tag} label={`#${tag}`} active={chosen.includes(tag)} onClick={() => toggle(tag)} />
        ))}
      </div>
      <Field label="Card label (optional)">
        <input value={label} onChange={e => setLabel(e.target.value)} placeholder={chosen.length ? chosen.map(t => `#${t}`).join(' ') : 'e.g. Stone craft'} style={S.inputStyle} />
      </Field>
      <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 16 }}>
        <BtnGhost onClick={onClose}>Cancel</BtnGhost>
        <BtnPrimary onClick={create}>{busy ? 'Adding…' : 'Add filter card'}</BtnPrimary>
      </div>
    </CommsModal>
  );
}

// ── ASSIGN TO THEME MODAL ──────────────────────────────────────────────────
// Multi-select target picker covering every theme section: plain themes,
// material sub-tabs, practice studio/monograph, and each collaborator.
// Used from the project press kit and from theme sections themselves.

const MATERIAL_SECTIONS = [['materials', 'Materials'], ['details', 'Details'], ['craft', 'Craft']];
const PRACTICE_SECTIONS = [['studio', 'Studio & bios'], ['monograph', 'Monograph']];

export function AssignToThemeModal({ assets, kitSchema, excludeTarget, onClose, onDone, showToast }) {
  const [themes, setThemes] = useState(null);
  const [collaborators, setCollaborators] = useState([]);
  const [picked, setPicked] = useState({}); // `${themeId}::${section||''}` -> {themeId, section, label}
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    Promise.all([
      comms.from('themes').select('comms_id, label, sort_order').order('sort_order'),
      comms.from('collaborators').select('comms_id, name').order('name'),
    ]).then(([t, c]) => { setThemes(t.data || []); setCollaborators(c.data || []); });
  }, []);

  const targets = [];
  (themes || []).forEach(t => {
    if (t.comms_id === 'material') {
      MATERIAL_SECTIONS.forEach(([sec, label]) => targets.push({ themeId: t.comms_id, section: sec, group: t.label, label }));
    } else if (t.comms_id === 'practice') {
      PRACTICE_SECTIONS.forEach(([sec, label]) => targets.push({ themeId: t.comms_id, section: sec, group: t.label, label }));
    } else if (t.comms_id === 'collaborators') {
      collaborators.forEach(c => targets.push({ themeId: t.comms_id, section: c.comms_id, group: t.label, label: c.name }));
    } else {
      targets.push({ themeId: t.comms_id, section: null, group: null, label: t.label });
    }
  });
  const keyOf = tg => `${tg.themeId}::${tg.section || ''}`;
  const excludeKey = excludeTarget ? `${excludeTarget.themeId}::${excludeTarget.section || ''}` : null;
  const visible = targets.filter(tg => keyOf(tg) !== excludeKey);
  const pickedList = Object.values(picked);

  const toggle = tg => setPicked(p => {
    const k = keyOf(tg);
    const next = { ...p };
    if (next[k]) delete next[k]; else next[k] = tg;
    return next;
  });

  // Highlights are drawn as pictures, so documents other than PDFs (a .docx
  // picked for sharing in the press kit) are left out.
  const assignable = assets.filter(a => !a.isFile);
  const skippedCount = assets.length - assignable.length;

  const assign = async () => {
    if (busy || pickedList.length === 0) return;
    if (assignable.length === 0) { showToast('Only documents selected — themes accept images, videos and PDFs'); return; }
    setBusy(true);
    try {
      for (const tg of pickedList) {
        await addAssetsToTheme(tg.themeId, tg.section, assignable, kitSchema, themeTagsFor(tg.themeId, tg.section, tg.label));
      }
      onDone(pickedList);
    } catch (e) {
      showToast(e.message, 'error');
      setBusy(false);
    }
  };

  const groups = [];
  visible.forEach(tg => {
    const g = groups.find(x => x.name === tg.group);
    if (g) g.items.push(tg); else groups.push({ name: tg.group, items: [tg] });
  });

  return (
    <CommsModal title="Assign to theme" onClose={onClose}>
      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginBottom: 16 }}>
        {assignable.map(a => a.thumbUrl
          ? <img key={a.path} src={a.thumbUrl} alt={a.name} title={a.name} loading="lazy" style={{ width: 52, height: 52, objectFit: 'cover', borderRadius: 2 }} />
          : <span key={a.path} title={a.name} style={{ width: 52, height: 52, borderRadius: 2, background: '#E8E5DD', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9A988F' }}><Film size={18} /></span>
        )}
      </div>
      {themes === null && <div style={S.meta}>Loading themes…</div>}
      <div style={{ maxHeight: 340, overflowY: 'auto' }}>
        {groups.map((g, gi) => (
          <div key={g.name || gi} style={{ marginBottom: 10 }}>
            {g.name && <div style={{ ...S.eyebrow, fontSize: 9, marginBottom: 4 }}>{g.name}</div>}
            {g.items.map(tg => {
              const k = keyOf(tg);
              return (
                <label key={k} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0', cursor: 'pointer', fontSize: 13.5, color: '#1B1B19' }}>
                  <input type="checkbox" checked={!!picked[k]} onChange={() => toggle(tg)} style={{ accentColor: GOLD, cursor: 'pointer' }} />
                  {tg.label}
                </label>
              );
            })}
          </div>
        ))}
      </div>
      {skippedCount > 0 && (
        <div style={{ ...S.meta, marginTop: 10, color: '#8A6A2A' }}>
          {skippedCount} document{skippedCount === 1 ? '' : 's'} will be skipped — themes accept images, videos and PDFs only.
        </div>
      )}
      <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 18 }}>
        <BtnGhost onClick={onClose}>Cancel</BtnGhost>
        <BtnPrimary onClick={assign}>
          {busy ? 'Assigning…' : `Assign ${assignable.length} file${assignable.length === 1 ? '' : 's'}${pickedList.length ? ` to ${pickedList.length} theme${pickedList.length === 1 ? '' : 's'}` : ''}`}
        </BtnPrimary>
      </div>
    </CommsModal>
  );
}

// ── THEME ASSETS SECTION ───────────────────────────────────────────────────

export function ThemeAssetsSection({ themeId, section = null, sectionLabel = '', projects = [], canEdit, showToast, defaultCollapsed = false }) {
  const [collapsed, setCollapsed] = useState(defaultCollapsed);
  const [folders, setFolders] = useState(null);
  const [assets, setAssets] = useState([]);
  const [filterAssets, setFilterAssets] = useState({}); // filter card comms_id -> asset_tags rows
  const [rev, setRev] = useState(0);
  const [kitSchema, setKitSchema] = useState(PRESS_KIT_DEFAULT_SCHEMA);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const [tagModalOpen, setTagModalOpen] = useState(false);
  const [lightboxEntry, setLightboxEntry] = useState(null);
  const [postAssign, setPostAssign] = useState(null); // { posts } once social_cal is loaded
  const [themeAssignOpen, setThemeAssignOpen] = useState(false);
  // Same image-version preference the project press kits use; it is set there,
  // on the Press Kit & Assets settings panel.
  const [versionPref] = useState(readVersionPref);
  const { sectionExpanded, sectionFiles, toggleSectionFiles } = usePressFolderBrowser(versionPref);
  const { selectMode, selection, selectedList, toggleSelectMode, exitSelectMode } = useAssetSelection();
  const tagsByAsset = useAssetTags(rev);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));
  const bump = () => setRev(r => r + 1);

  useEffect(() => {
    if (collapsed) return;
    let cancelled = false;
    const scoped = q => (section == null ? q.is('section', null) : q.eq('section', section));
    Promise.all([
      scoped(comms.from('theme_folders').select('*').eq('theme_id', themeId)).order('sort_order'),
      scoped(comms.from('theme_assets').select('*').eq('theme_id', themeId)).order('sort_order'),
    ]).then(([f, a]) => {
      if (cancelled) return;
      setFolders(f.data || []);
      setAssets(a.data || []);
      // Filter cards resolve their matching assets eagerly — that's their point.
      const filterCards = (f.data || []).filter(r => Array.isArray(r.filter_tags) && r.filter_tags.length);
      Promise.all(filterCards.map(r =>
        comms.from('asset_tags').select('*').contains('tags', r.filter_tags)
          .order('updated_at', { ascending: false })
          .then(({ data }) => [r.comms_id, data || []])
      )).then(entries => { if (!cancelled) setFilterAssets(Object.fromEntries(entries)); });
    });
    return () => { cancelled = true; };
  }, [themeId, section, rev, collapsed]);

  useEffect(() => {
    supabase.from('app_settings').select('value').eq('key', 'pressKitFolderSchema').maybeSingle().then(({ data: row }) => {
      if (!row?.value) return;
      try {
        const v = typeof row.value === 'string' ? JSON.parse(row.value) : row.value;
        if (Array.isArray(v) && v.length) setKitSchema(v);
      } catch {}
    });
  }, []);

  if (collapsed) {
    // Same block-title header as the expanded section, so it reads as a real
    // section rather than a link; contents lazy-load on first expand.
    return (
      <div style={{ marginBottom: 18 }}>
        <div onClick={() => setCollapsed(false)} title="Show press kit & assets"
          style={{ ...S.blockTitle, cursor: 'pointer', marginBottom: 0 }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <ChevronRight size={11} /> Press Kit &amp; Assets
          </span>
        </div>
      </div>
    );
  }

  const folderList = folders || [];
  const existingPaths = new Set(folderList.map(f => f.dropbox_path));
  const suggestions = (projects || [])
    .filter(p => p.project_code && Array.isArray(p.themes) && p.themes.includes(themeId))
    .sort((a, b) => (a.project_code || '').localeCompare(b.project_code || ''))
    .map(p => ({ label: `${p.project_code} — ${p.name}`, path: `${BRIO_PRESS_BASE}/${p.project_code}_${(p.name || '').replace(/\//g, '_')}` }));

  const withProjectCode = list => list.map(a => ({
    ...a,
    projectCode: a.projectCode || projectCodeForPath(a.path, folderList, projects),
  }));

  const linkFolder = async (path) => {
    const label = path.split('/').pop();
    const top = path.startsWith(BRIO_PRESS_BASE + '/') ? path.slice(BRIO_PRESS_BASE.length + 1).split('/')[0] : '';
    const project = (projects || []).find(p => p.project_code && top.startsWith(`${p.project_code}_`));
    try {
      await commsInsert('theme_folders', {
        comms_id: `tf-${Date.now()}`,
        theme_id: themeId,
        section: section ?? null,
        project_code: project?.project_code || null,
        dropbox_path: path,
        label,
        sort_order: folderList.length,
      });
      bump();
      showToast('Folder linked');
    } catch (e) { showToast(e.message, 'error'); }
  };

  const unlinkFolder = async (row) => {
    if (!window.confirm(`Remove "${row.label || row.dropbox_path || 'this card'}" from this theme? Files in Dropbox are not touched.`)) return;
    try { await commsDelete('theme_folders', 'comms_id', row.comms_id); bump(); showToast('Removed'); } catch (e) { showToast(e.message, 'error'); }
  };

  const removeHighlight = async (a) => {
    try { await commsDelete('theme_assets', 'comms_id', a.comms_id); bump(); } catch (e) { showToast(e.message, 'error'); }
  };

  const saveCaption = async (a, caption) => {
    try { await commsUpdate('theme_assets', 'comms_id', a.comms_id, { caption }); bump(); } catch (e) { showToast(e.message, 'error'); }
  };

  const createFilterCard = async (tags, label) => {
    try {
      await commsInsert('theme_folders', {
        comms_id: `tfl-${Date.now()}`,
        theme_id: themeId,
        section: section ?? null,
        dropbox_path: null,
        filter_tags: tags,
        label,
        sort_order: (folders || []).length,
      });
      setFilterModalOpen(false);
      bump();
      showToast('Filter card added');
    } catch (e) { showToast(e.message, 'error'); }
  };

  const openCachedAsset = async (row) => {
    const name = row.file_name || row.dropbox_path.split('/').pop();
    try {
      const { link } = await callEdgeFunction('get-press-file-link', { path: row.dropbox_path });
      setLightboxEntry({ entries: [{ url: link, path: row.dropbox_path, name, isPdf: isPdf(name), isVideo: !!row.is_video }], index: 0 });
    } catch (e) { showToast(e.message, 'error'); }
  };

  const handleHighlightDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIdx = assets.findIndex(a => a.comms_id === active.id);
    const newIdx = assets.findIndex(a => a.comms_id === over.id);
    const reordered = arrayMove(assets, oldIdx, newIdx);
    setAssets(reordered);
    try {
      await Promise.all(reordered.map((a, i) => a.sort_order === i ? null : commsUpdate('theme_assets', 'comms_id', a.comms_id, { sort_order: i })).filter(Boolean));
    } catch (e) { showToast(e.message, 'error'); bump(); }
  };

  const addToHighlights = async () => {
    // Matched on the file id, not the path: a highlight pinned before its file
    // was renamed still holds the same id, so it reads as already-there rather
    // than being pinned a second time under its new name.
    const chosen = withProjectCode(selectedList);
    const fresh = chosen.filter(a => a.fileId && !assets.some(x => x.dropbox_file_id === a.fileId));
    const skipped = chosen.length - fresh.length;
    if (fresh.length === 0) { showToast('Already in highlights'); return; }
    try {
      const added = await addAssetsToTheme(themeId, section, fresh, kitSchema, themeTagsFor(themeId, section, sectionLabel));
      bump();
      exitSelectMode();
      showToast(`${added} added to highlights${skipped ? ` (${skipped} skipped)` : ''}`);
    } catch (e) { showToast(e.message, 'error'); }
  };

  const openPostAssign = async () => {
    try {
      const { data: posts } = await comms.from('social_cal')
        .select('comms_id, title, post_date, status').order('post_date', { ascending: false });
      setPostAssign({ posts: posts || [] });
    } catch (e) { showToast(e.message, 'error'); }
  };

  return (
    <div style={{ marginBottom: 28 }}>
      <Lightbox entries={lightboxEntry?.entries} index={lightboxEntry?.index} onClose={() => setLightboxEntry(null)} />
      {pickerOpen && (
        <LinkFolderModal
          suggestions={suggestions}
          existingPaths={existingPaths}
          onLink={linkFolder}
          onClose={() => setPickerOpen(false)}
        />
      )}
      {postAssign && (
        <AssignToPostModal
          assets={withProjectCode(selectedList)}
          posts={postAssign.posts}
          project={{ project_code: null }}
          kitSchema={kitSchema}
          onClose={() => setPostAssign(null)}
          onDone={() => {
            setPostAssign(null);
            exitSelectMode();
            showToast(`${selectedList.length} file${selectedList.length === 1 ? '' : 's'} added — open the Social tab to build the post`);
          }}
          showToast={showToast}
        />
      )}
      {themeAssignOpen && (
        <AssignToThemeModal
          assets={withProjectCode(selectedList)}
          kitSchema={kitSchema}
          excludeTarget={{ themeId, section }}
          onClose={() => setThemeAssignOpen(false)}
          onDone={(targets) => {
            setThemeAssignOpen(false);
            exitSelectMode();
            bump();
            showToast(`Assigned to ${targets.length} theme${targets.length === 1 ? '' : 's'}`);
          }}
          showToast={showToast}
        />
      )}
      {filterModalOpen && (
        <AddFilterModal onCreate={createFilterCard} onClose={() => setFilterModalOpen(false)} />
      )}
      {tagModalOpen && (
        <TagAssetsModal
          assets={withProjectCode(selectedList)}
          onClose={() => setTagModalOpen(false)}
          onDone={() => {
            setTagModalOpen(false);
            exitSelectMode();
            bump();
            showToast('Hashtags saved');
          }}
          showToast={showToast}
        />
      )}
      {selectMode && (
        <SelectBar
          count={selectedList.length}
          emptyText="Click images, videos, or PDFs to select them"
          actions={[
            { label: 'Add to highlights', onClick: addToHighlights },
            { label: 'Assign to post', onClick: openPostAssign },
            { label: 'Assign to theme', onClick: () => setThemeAssignOpen(true) },
            { label: 'Hashtags', onClick: () => setTagModalOpen(true) },
          ]}
          onCancel={exitSelectMode}
        />
      )}

      <div style={S.blockTitle}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          {defaultCollapsed && (
            <button onClick={() => setCollapsed(true)} title="Collapse"
              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: 'inherit', display: 'flex' }}>
              <ChevronDown size={11} />
            </button>
          )}
          Press Kit &amp; Assets
        </span>
        {canEdit && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <IconBtn onClick={() => setPickerOpen(true)} title="Link a Dropbox folder"><FolderPlus size={11} /></IconBtn>
            <IconBtn onClick={() => setFilterModalOpen(true)} title="Add a hashtag filter card"><Hash size={11} /></IconBtn>
            <IconBtn onClick={toggleSelectMode} active={selectMode} title={selectMode ? 'Exit selection' : 'Select assets for highlights, a post, another theme, or hashtags'}><ImagePlus size={11} /></IconBtn>
          </div>
        )}
      </div>

      {folders === null && <div style={S.meta}>Loading…</div>}

      {folders !== null && (
        <>
          {/* HIGHLIGHTS */}
          <div style={{ marginBottom: 14 }}>
            <div style={{ ...S.eyebrow, fontSize: 9, marginBottom: 6 }}>Highlights</div>
            {assets.length === 0 && <EmptyNote text={canEdit ? 'No highlights yet — select images from a linked folder below or from a project press kit.' : 'No highlights yet.'} />}
            {assets.length > 0 && (
              <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleHighlightDragEnd}>
                <SortableContext items={assets.map(a => a.comms_id)} strategy={rectSortingStrategy}>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(110px, 1fr))', gap: 4 }}>
                    {assets.map(a => (
                      <SortableHighlightTile
                        key={a.comms_id}
                        asset={a}
                        canEdit={canEdit}
                        selectMode={selectMode}
                        selection={selection}
                        projects={projects}
                        onOpen={openCachedAsset}
                        onRemove={removeHighlight}
                        onSaveCaption={saveCaption}
                      />
                    ))}
                  </div>
                </SortableContext>
              </DndContext>
            )}
          </div>

          {/* LINKED FOLDERS & HASHTAG FILTERS */}
          <div>
            <div style={{ ...S.eyebrow, fontSize: 9, marginBottom: 6 }}>Linked folders &amp; filters</div>
            {folderList.length === 0 && <EmptyNote text={canEdit ? 'No folders or filters yet — use the folder button to pick from BRIO-press, or the # button to add a hashtag filter.' : 'No folders linked yet.'} />}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 14 }}>
              {folderList.map(row => {
                if (Array.isArray(row.filter_tags) && row.filter_tags.length) {
                  const matches = filterAssets[row.comms_id] || [];
                  return (
                    <div key={row.comms_id} style={{ ...S.card, marginBottom: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          {canEdit
                            ? <InlineInput value={row.label || ''} onBlurSave={v => commsUpdate('theme_folders', 'comms_id', row.comms_id, { label: v }).then(bump).catch(e => showToast(e.message, 'error'))} placeholder="Label…" style={{ ...S.eyebrow, border: 'none', background: 'transparent', padding: 0 }} />
                            : <span style={S.eyebrow}>{row.label || row.filter_tags.map(t => `#${t}`).join(' ')}</span>
                          }
                        </div>
                        {canEdit && (
                          <button onClick={() => unlinkFolder(row)} title="Remove filter card"
                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#B0A9A0', padding: 2, flexShrink: 0, lineHeight: 0 }}>
                            <Trash2 size={12} />
                          </button>
                        )}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 4, marginBottom: 8 }}>
                        {[...row.filter_tags].sort((a, b) => a.localeCompare(b)).map(t => <Chip key={t} label={`#${t}`} cls="pipe" />)}
                        <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 9, color: '#9E9C96' }}>{matches.length} asset{matches.length === 1 ? '' : 's'}</span>
                      </div>
                      {matches.length === 0
                        ? <EmptyNote text="No assets carry all these hashtags yet." />
                        : <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 4 }}>
                            {matches.map(m => (
                              <FilterAssetTile key={m.dropbox_path} row={m} selectMode={selectMode} selection={selection} onOpen={openCachedAsset} />
                            ))}
                          </div>
                      }
                    </div>
                  );
                }
                const fKey = `tf-${row.comms_id}`;
                const expanded = !!sectionExpanded[fKey];
                const project = (projects || []).find(p => p.project_code === row.project_code);
                return (
                  <div key={row.comms_id} style={{ ...S.card, marginBottom: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        {canEdit
                          ? <InlineInput value={row.label || ''} onBlurSave={v => commsUpdate('theme_folders', 'comms_id', row.comms_id, { label: v }).then(bump).catch(e => showToast(e.message, 'error'))} placeholder="Label…" style={{ ...S.eyebrow, border: 'none', background: 'transparent', padding: 0 }} />
                          : <span style={S.eyebrow}>{row.label || row.dropbox_path.split('/').pop()}</span>
                        }
                      </div>
                      {canEdit && (
                        <button onClick={() => unlinkFolder(row)} title="Unlink folder"
                          style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#B0A9A0', padding: 2, flexShrink: 0, lineHeight: 0 }}>
                          <Trash2 size={12} />
                        </button>
                      )}
                      <button onClick={() => toggleSectionFiles(fKey, row.dropbox_path)}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 1, color: '#9E9C96', lineHeight: 1, flexShrink: 0 }}>
                        {expanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                      </button>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: expanded ? 8 : 0 }}>
                      <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 9, color: '#9E9C96', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, minWidth: 0 }}>
                        {row.dropbox_path.startsWith(BRIO_PRESS_BASE + '/') ? row.dropbox_path.slice(BRIO_PRESS_BASE.length + 1) : row.dropbox_path}
                      </span>
                      {project && <Chip label={project.project_code} cls="line" />}
                    </div>
                    {expanded && (
                      <div style={{ borderTop: '1px solid rgba(27,27,25,.08)', paddingTop: 8 }}>
                        <FolderTree
                          folderKey={fKey}
                          folderPath={row.dropbox_path}
                          sectionFiles={sectionFiles}
                          sectionExpanded={sectionExpanded}
                          onToggleFiles={toggleSectionFiles}
                          versionPref={versionPref}
                          imgCols={3}
                          onOpenLightbox={setLightboxEntry}
                          selection={selection}
                          tagsByAsset={tagsByAsset}
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
